import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";
import type {
  PartnerMenuItem,
  PartnerMenuItemCreate,
  PartnerMenuItemUpdate,
  PartnerMenuItemProduct,
  PartnerMenuItemProductCreate,
  PartnerMenuItemProductUpdate,
  PartnerMenuItemWithProducts,
  StoppedProductOverrideRequest,
} from "@/app/api/types/partnerMenu";

export default class APIPartnerMenu extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Menu Item endpoints

  // Get all menu items for a partner with products
  getPartnerMenuItems = async (
    partnerId: UUID,
  ): Promise<PartnerMenuItemWithProducts[]> => {
    let res = await this._fetch({
      url: `/partner-menu/partners/${partnerId}/menu-items`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get a single menu item with products
  getMenuItem = async (
    menuItemId: UUID,
  ): Promise<PartnerMenuItemWithProducts> => {
    let res = await this._fetch({
      url: `/partner-menu/menu-items/${menuItemId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create a new menu item
  createMenuItem = async (
    partnerId: UUID,
    data: PartnerMenuItemCreate,
  ): Promise<PartnerMenuItem> => {
    let res = await this._fetch({
      url: `/partner-menu/partners/${partnerId}/menu-items`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update a menu item
  updateMenuItem = async (
    menuItemId: UUID,
    data: PartnerMenuItemUpdate,
  ): Promise<PartnerMenuItem> => {
    let res = await this._fetch({
      url: `/partner-menu/menu-items/${menuItemId}`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Delete a menu item
  deleteMenuItem = async (menuItemId: UUID): Promise<PartnerMenuItem> => {
    let res = await this._fetch({
      url: `/partner-menu/menu-items/${menuItemId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Product Mapping endpoints

  // Get products for a menu item
  getMenuItemProducts = async (
    menuItemId: UUID,
  ): Promise<PartnerMenuItemProduct[]> => {
    let res = await this._fetch({
      url: `/partner-menu/menu-items/${menuItemId}/products`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Add a product to a menu item
  addProductToMenuItem = async (
    menuItemId: UUID,
    data: PartnerMenuItemProductCreate,
  ): Promise<PartnerMenuItemProduct> => {
    let res = await this._fetch({
      url: `/partner-menu/menu-items/${menuItemId}/products`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update a product mapping
  updateProductMapping = async (
    mappingId: UUID,
    data: PartnerMenuItemProductUpdate,
  ): Promise<PartnerMenuItemProduct> => {
    let res = await this._fetch({
      url: `/partner-menu/menu-products/${mappingId}`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Remove a product from a menu item
  removeProductFromMenuItem = async (
    mappingId: UUID,
  ): Promise<PartnerMenuItemProduct> => {
    let res = await this._fetch({
      url: `/partner-menu/menu-products/${mappingId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Override stopped status
  overrideStoppedStatus = async (
    mappingId: UUID,
    data: StoppedProductOverrideRequest,
  ): Promise<PartnerMenuItemProduct> => {
    let res = await this._fetch({
      url: `/partner-menu/menu-products/${mappingId}/stopped-override`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
